import type { Medicine } from "@/entities/medicine/api/medicines";
import { toApiError, type PaginatedResponse, type PaginationMeta } from "@/shared/api/common";
import { http } from "@/shared/api/http";

export type CertifiedMedicineCountry = {
  id: string;
  nameUz: string | null;
  nameRu: string | null;
  nameEn: string | null;
};

export type CertifiedMedicineOrderedByOrganization = {
  id: string;
  name: string;
  slug: string | null;
  logoSm: string | null;
};

export type CertifiedMedicine = {
  id: number;
  year: number | null;
  productName: string;
  batchSeries: string | null;
  certifyingBody: string | null;
  manufacturer: string | null;
  manufacturerCountry: string | null;
  countryId: string | null;
  country: CertifiedMedicineCountry | null;
  medicineId: string | null;
  medicine: Medicine | null;
  orderedBy: string | null;
  orderedByOrganization: CertifiedMedicineOrderedByOrganization | null;
  shelfLifeDate: string | null;
  shelfLifeRaw: string | null;
  registrationNumber: string | null;
  certificateIssuedAt: string | null;
  crawledAt: string;
  createdAt: string;
  updatedAt: string;
};

export type CertifiedMedicineSortField =
  | "product_name"
  | "year"
  | "manufacturer"
  | "manufacturer_country"
  | "certificate_issued_at"
  | "shelf_life_date"
  | "created_at";

export type CertifiedMedicineSortOrder = "asc" | "desc";

export type CertifiedMedicinesQueryParams = {
  page?: number;
  limit?: number;
  id?: number | null;
  search?: string;
  year?: number | null;
  countryId?: string | null;
  /** UUID of a medicine — backend filters certificates to those linked to this medicine. */
  medicineId?: string | null;
  certifyingBody?: string | null;
  shelfLifeFrom?: string | null;
  shelfLifeTo?: string | null;
  sortBy?: CertifiedMedicineSortField | null;
  sortOrder?: CertifiedMedicineSortOrder | null;
};

type CertifiedMedicineCountryApi = {
  id: string;
  name_uz: string | null;
  name_ru: string | null;
  name_en: string | null;
};

type CertifiedMedicineOrderedByOrganizationApi = {
  id: string;
  name: string;
  slug: string | null;
  logo_sm: string | null;
};

type CertifiedMedicineApi = {
  id: number;
  year: number | null;
  product_name: string;
  batch_series: string | null;
  certifying_body: string | null;
  manufacturer: string | null;
  manufacturer_country: string | null;
  country_id: string | null;
  country?: CertifiedMedicineCountryApi | null;
  medicine_id: string | null;
  medicine?: Medicine | null;
  ordered_by: string | null;
  ordered_by_organization?: CertifiedMedicineOrderedByOrganizationApi | null;
  shelf_life_date: string | null;
  shelf_life_raw: string | null;
  registration_number: string | null;
  certificate_issued_at: string | null;
  crawled_at: string;
  created_at: string;
  updated_at: string;
};

function toCertifiedMedicine(row: CertifiedMedicineApi): CertifiedMedicine {
  return {
    id: row.id,
    year: row.year,
    productName: row.product_name,
    batchSeries: row.batch_series,
    certifyingBody: row.certifying_body,
    manufacturer: row.manufacturer,
    manufacturerCountry: row.manufacturer_country,
    countryId: row.country_id,
    country: row.country
      ? {
          id: row.country.id,
          nameUz: row.country.name_uz,
          nameRu: row.country.name_ru,
          nameEn: row.country.name_en,
        }
      : null,
    medicineId: row.medicine_id ?? null,
    medicine: row.medicine ?? null,
    orderedBy: row.ordered_by,
    orderedByOrganization: row.ordered_by_organization
      ? {
          id: row.ordered_by_organization.id,
          name: row.ordered_by_organization.name,
          slug: row.ordered_by_organization.slug,
          logoSm: row.ordered_by_organization.logo_sm,
        }
      : null,
    shelfLifeDate: row.shelf_life_date,
    shelfLifeRaw: row.shelf_life_raw,
    registrationNumber: row.registration_number,
    certificateIssuedAt: row.certificate_issued_at,
    crawledAt: row.crawled_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export type CertifyingBodiesQueryParams = {
  /** Fuzzy substring match (case-insensitive). */
  q?: string;
  /** Server caps this at 100; defaults to 10 if omitted. */
  limit?: number;
};

export async function fetchCertifyingBodies(params: CertifyingBodiesQueryParams = {}): Promise<string[]> {
  const trimmed = typeof params.q === "string" ? params.q.trim() : "";
  try {
    const response = await http.get<{ data: string[] }>("/certified-medicines/certifying-bodies", {
      params: {
        q: trimmed || undefined,
        limit: params.limit,
      },
    });
    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    return rows.filter((value): value is string => typeof value === "string" && value.trim().length > 0);
  } catch (error) {
    throw toApiError(error, "Unable to load certifying bodies.");
  }
}

export async function fetchCertifiedMedicines(
  params: CertifiedMedicinesQueryParams = {},
): Promise<{ data: CertifiedMedicine[]; pagination: PaginationMeta }> {
  const normalizedSearch = typeof params.search === "string" ? params.search.trim() : "";

  try {
    const response = await http.get<PaginatedResponse<CertifiedMedicineApi>>("/certified-medicines", {
      params: {
        page: params.page,
        limit: params.limit,
        id: params.id ?? undefined,
        q: normalizedSearch || undefined,
        year: params.year ?? undefined,
        country_id: params.countryId ?? undefined,
        medicine_id: params.medicineId ?? undefined,
        certifying_body: params.certifyingBody ?? undefined,
        shelf_life_from: params.shelfLifeFrom ?? undefined,
        shelf_life_to: params.shelfLifeTo ?? undefined,
        sort_by: params.sortBy ?? undefined,
        sort_order: params.sortOrder ?? undefined,
      },
    });

    const rows = Array.isArray(response.data?.data) ? response.data.data : [];
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;

    return {
      data: rows.map(toCertifiedMedicine),
      pagination: response.data?.pagination ?? {
        page,
        limit,
        total: rows.length,
        totalPages: Math.max(1, Math.ceil(rows.length / limit)),
      },
    };
  } catch (error) {
    throw toApiError(error, "Unable to load certified medicines.");
  }
}
